<template>
  <div class="activity-page">
    <header class="activity-header">
      <button
        class="header-back"
        type="button"
        :aria-label="$t('activityTasks.back')"
        @click="$router.push('/user')"
      >
        <van-icon name="arrow-left" />
      </button>
      <h1>{{ $t('activityTasks.title') }}</h1>
      <span class="header-space" />
    </header>

    <main class="activity-content">
      <section class="activity-intro">
        <!-- <span class="intro-kicker">{{ $t('activityTasks.orderRewards') }}</span> -->
        <h2>{{ $t('activityTasks.heading') }}</h2>
        <p>{{ $t('activityTasks.subtitle') }}</p>
      </section>

      <div
        v-if="loading && !tasks.length"
        class="state-card"
      >
        {{ $t('activityTasks.loading') }}
      </div>
      <div
        v-else-if="error && !tasks.length"
        class="state-card"
      >
        <p>{{ $t('activityTasks.loadFailed') }}</p>
        <button
          type="button"
          @click="loadTasks(1)"
        >
          {{ $t('activityTasks.retry') }}
        </button>
      </div>
      <div
        v-else-if="!tasks.length"
        class="state-card"
      >
        {{ $t('activityTasks.empty') }}
      </div>

      <section
        v-for="task in tasks"
        :key="task.id"
        class="task-card"
      >
        <div class="task-top">
          <div class="task-icon">
            <van-icon name="gift-o" />
          </div>
          <span class="task-type">{{ $t('activityTasks.orderRewards') }}</span>
          <span
            v-if="task.claimed"
            class="task-finished"
          >{{ $t('activityTasks.claimed') }}</span>
        </div>
        <h2 class="task-title">
          {{ task.title }}
        </h2>
        <p class="task-description">
          {{ task.content }}
        </p>
        <div class="task-divider" />
        <div class="progress-heading">
          <span>{{ $t('activityTasks.progress') }}</span>
          <span>{{ task.progress_percent }}%</span>
        </div>
        <div
          class="progress-track"
          role="progressbar"
          :aria-valuenow="task.progress_percent"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            class="progress-fill"
            :style="{ width: task.progress_percent + '%' }"
          />
        </div>
        <div class="progress-count">
          {{ $t('activityTasks.completedOrders') }}
          <strong>{{ task.completed_orders }} / {{ task.required_orders }}</strong>
        </div>
        <div class="task-bottom">
          <div class="reward">
            <span>{{ $t('activityTasks.reward') }}</span>
            <strong>{{ utils_moneyMark() }} {{ task.reward_amount }}</strong>
          </div>
          <button
            class="task-action"
            :class="{ 'is-claimed': task.claimed }"
            type="button"
            :disabled="task.claimed || claimingId !== null"
            @click="onTaskAction(task)"
          >
            {{ actionLabel(task) }}
          </button>
        </div>
      </section>

      <button
        v-if="hasMore && !error"
        class="load-more"
        type="button"
        :disabled="loading"
        @click="loadTasks(page + 1, true)"
      >
        {{ loading ? $t('activityTasks.loading') : $t('activityTasks.loadMore') }}
      </button>
      <button
        v-if="error && tasks.length"
        class="load-more"
        type="button"
        @click="loadTasks(page + 1, true)"
      >
        {{ $t('activityTasks.retry') }}
      </button>
    </main>
  </div>
</template>

<script>
import Vue from 'vue';
import { Icon, Toast } from 'vant';
import Fetch from '../../utils/fetch';

Vue.use(Icon).use(Toast);

export default {
  name: 'ActivityTasks',
  data() {
    return {
      tasks: [],
      page: 0,
      hasMore: false,
      loading: false,
      error: false,
      claimingId: null,
    };
  },
  created() {
    this.$parent.footer('user', true);
  },
  mounted() {
    this.loadTasks(1);
  },
  methods: {
    loadTasks(page, append = false) {
      if (this.loading) return;
      this.loading = true;
      this.error = false;
      Fetch('/user/activity_tasks', { page }).then((response) => {
        const result = response.data || {};
        const rows = Array.isArray(result.list) ? result.list : [];
        this.tasks = append ? this.tasks.concat(rows) : rows;
        this.page = page;
        this.hasMore = Boolean(result.has_more);
      }).catch(() => {
        this.error = true;
      }).finally(() => {
        this.loading = false;
      });
    },
    actionLabel(task) {
      if (task.claimed) return this.$t('activityTasks.claimed');
      if (this.claimingId === task.id) return this.$t('activityTasks.claiming');
      return task.completed_orders >= task.required_orders
        ? this.$t('activityTasks.claimReward')
        : this.$t('activityTasks.goComplete');
    },
    onTaskAction(task) {
      if (task.claimed || this.claimingId !== null) return;
      if (task.completed_orders < task.required_orders) {
        this.$router.push('/grap');
        return;
      }
      this.claimingId = task.id;
      Fetch('/user/claim_activity_reward', { id: task.id }).then(() => {
        task.claimed = true;
        Toast.success(this.$t('activityTasks.claimSuccess'));
      }).catch(() => {
        // Fetch 已展示后端的本地化错误提示，页面保持可重试。
      }).finally(() => {
        this.claimingId = null;
      });
    },
  },
};
</script>

<style lang="less" scoped>
.activity-page {
  box-sizing: border-box;
  min-height: 100vh;
  padding-bottom: 100px;
  color: #292524;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.activity-header {
  box-sizing: border-box;
  display: flex;
  height: 56px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee8e5;
  background: #fff;
}

.activity-header h1 {
  margin: 0;
  color: #222;
  font-size: 16px;
  font-weight: 700;
}

.header-back,
.header-space {
  box-sizing: border-box;
  width: 56px;
  height: 56px;
}

.header-back {
  border: 0;
  background: transparent;
  color: #292524;
  font-size: 20px;
}

.activity-content {
  max-width: 600px;
  margin: 0 auto;
  padding: 26px 18px 0;
}

.activity-intro {
  margin-bottom: 22px;
}

.intro-kicker {
  color: #ff385c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .08em;
}

.activity-intro h2 {
  margin: 9px 0 7px;
  color: #292524;
  font-size: 16px;
  line-height: 1.45;
}

.activity-intro p {
  margin: 0;
  color: #686360;
  font-size: 14px;
  line-height: 1.65;
  overflow-wrap: anywhere;
}

.task-card,
.state-card {
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 18px;
  border: 1px solid #eee7e4;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(45, 36, 34, .09);
}

.task-card {
  padding: 20px;
}

.task-top,
.progress-heading,
.progress-count,
.task-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.task-top {
  justify-content: flex-start;
  gap: 8px;
}

.task-icon {
  display: flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #fff0f1;
  color: #ff385c;
  font-size: 20px;
}

.task-type,
.task-finished,
.progress-heading,
.progress-count,
.reward span {
  font-size: 12px;
}

.task-type {
  color: #76706d;
  font-weight: 600;
  letter-spacing: .04em;
}

.task-finished {
  margin-left: auto;
  color: #79726f;
}

.task-title {
  overflow-wrap: anywhere;
  margin: 17px 0 8px;
  color: #292524;
  font-size: 16px;
  line-height: 1.45;
}

.task-description {
  margin: 0;
  color: #686360;
  font-size: 14px;
  line-height: 1.65;
  overflow-wrap: anywhere;
  white-space: pre-line;
}

.task-divider {
  height: 1px;
  margin: 18px 0;
  background: #f0ecea;
}

.progress-heading {
  margin-bottom: 10px;
  color: #686360;
}

.progress-track {
  overflow: hidden;
  height: 8px;
  border-radius: 999px;
  background: #f4eceb;
}

.progress-fill {
  height: 100%;
  border-radius: inherit;
  background: #ff385c;
  transition: width .25s ease;
}

.progress-count {
  margin-top: 10px;
  color: #76706d;
}

.progress-count strong {
  color: #292524;
  font-size: 14px;
}

.task-bottom {
  gap: 12px;
  margin-top: 20px;
  padding: 12px;
  border-radius: 14px;
  background: #fff8f7;
}

.reward {
  min-width: 0;
}

.reward span,
.reward strong {
  display: block;
}

.reward span {
  color: #76706d;
}

.reward strong {
  margin-top: 4px;
  color: #ff385c;
  font-size: 16px;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.task-action {
  flex: 0 0 auto;
  min-width: 112px;
  min-height: 44px;
  padding: 0 14px;
  border: 0;
  border-radius: 12px;
  background: #ff385c;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  box-shadow: 0 5px 12px rgba(255, 56, 92, .18);
}

.task-action.is-claimed,
.task-action:disabled {
  background: #efecea;
  box-shadow: none;
  color: #857e7a;
}

.state-card {
  padding: 36px 18px;
  color: #686360;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
}

.state-card p {
  margin: 0 0 12px;
}

.state-card button,
.load-more {
  border: 0;
  background: transparent;
  color: #ff385c;
  font-size: 14px;
  font-weight: 700;
}

.load-more {
  display: block;
  width: 100%;
  min-height: 44px;
  margin: 8px 0 16px;
}

@media (max-width: 340px) {
  .activity-content { padding-right: 14px; padding-left: 14px; }
  .task-card { padding: 16px; }
  .task-bottom { flex-wrap: wrap; }
  .task-action { width: 100%; }
}
</style>
